// BlobUploadManager für Outlook Add-In - Direkter Upload zu Azure Blob Storage (Block Blob) mit SAS
class BlobUploadManager {
  constructor() {
    this.CHUNK_SIZE = 8 * 1024 * 1024
    this.MAX_CONCURRENCY = 6
    this.MAX_RETRIES = 3
    this.RETRY_DELAY_MS = 1000
  }

  get isDebug() {
    try {
      var ls = null
      try {
        ls = window.localStorage && window.localStorage.getItem("UPLOAD_DEBUG")
      } catch (e) {}
      var enabled = typeof ls === "string" && ["1", "true"].indexOf(ls.toLowerCase()) >= 0
      return enabled
    } catch (e) {
      return false
    }
  }

  debug() {
    if (!this.isDebug) return
    var args = ["[BlobUpload]"]
    for (var i = 0; i < arguments.length; i++) args.push(arguments[i])
    console.log.apply(console, args)
  }

  get apiBase() {
    // Zentrale Backend-URL: build-time aus .env (BACKEND_URL), siehe webpack.config.js. Kein Fallback.
    var configured = process.env.BACKEND_URL
    try {
      var explicit = null
      try {
        // Laufzeit-Override für lokales Testing: localStorage.setItem("UPLOAD_API_BASE", "http://localhost:4000")
        explicit = window.localStorage && window.localStorage.getItem("UPLOAD_API_BASE")
      } catch (e) {}
      if (explicit) {
        this.debug("apiBase (explicit)", explicit)
        return explicit
      }
      this.debug("apiBase (env)", configured)
      return configured
    } catch (e) {
      return configured
    }
  }

  async getSas(uploadId, ttlMinutes, options) {
    options = options || {}
    if (!ttlMinutes) ttlMinutes = 120
    this.debug("SAS anfordern", { uploadId: uploadId, ttlMinutes: ttlMinutes, options: options })
    var headers = {
      "Content-Type": "application/json",
      "X-Client-Type": "outlook-addin",
    }
    // Backend-Auth: unter "x-client-type: outlook-addin" prüft der Backend-
    // Middleware zwei dedizierte Header (x-addin-email + x-addin-token) gegen
    // die outlook_addin_tokens-Tabelle. NICHT "Authorization: Bearer" senden;
    // das schlägt in AUTH_ENFORCED=true fehl.
    try {
      var t = window.localStorage && window.localStorage.getItem("anolink_token")
      var e2 = window.localStorage && window.localStorage.getItem("anolink_email")
      if (e2) headers["x-addin-email"] = e2
      if (t) headers["x-addin-token"] = t
    } catch (e) {}
    var body = {
      uploadId: uploadId,
      ttlMinutes: ttlMinutes,
    }
    if (options.relativePath) {
      body.relativePath = options.relativePath
    }
    if (options.originalName) {
      body.originalName = options.originalName
    }
    if (options.contentType) {
      body.contentType = options.contentType
    }
    if (options.orgId) {
      body.orgId = options.orgId
    }
    if (options.userId) {
      body.userId = options.userId
    }
    if (options.accountName) {
      body.accountName = options.accountName
    }
    var res = await fetch(this.apiBase + "/getUploadSas", {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      var data = null
      try {
        data = await res.json()
      } catch (e) {
        data = {}
      }
      throw new Error((data && data.error) || "SAS-Token konnte nicht ausgestellt werden")
    }
    var json = await res.json()
    this.debug("SAS erhalten", {
      uploadUrlPrefix: json && json.uploadUrlPrefix ? json.uploadUrlPrefix : null,
      blobUrl: json && json.blobUrl ? json.blobUrl : null,
      expiresOn: json && json.expiresOn ? json.expiresOn : null,
    })
    return json
  }

  makeBlockId(index) {
    var id = "block-" + String(index).padStart(10, "0")
    return btoa(id)
  }

  buildAzureUrl(blobUrl, sasToken, extraQuery) {
    // Normalisiere und merge Query-Strings (verhindert doppelte ? oder &)
    var parts = String(blobUrl).split("?")
    var base = parts[0]
    var existingQs = parts.length > 1 ? parts.slice(1).join("?") : ""
    var tokenQs = (sasToken || "").replace(/^[?&]/, "")
    var extraQs = (extraQuery || "").replace(/^[?&]/, "")
    var all = []
    if (existingQs) all.push(existingQs)
    if (extraQs) all.push(extraQs)
    if (tokenQs) all.push(tokenQs)
    return base + (all.length ? "?" + all.join("&") : "")
  }

  // Kodiert beliebige Unicode-Werte ASCII-sicher für Azure-Blob-Metadaten-Header (x-ms-meta-*)
  encodeAsciiMeta(value) {
    try {
      if (value == null) return undefined
      var s = String(value)
      // Percent-Encoding erzeugt ausschließlich ASCII-Zeichen
      return encodeURIComponent(s)
    } catch (e) {
      return undefined
    }
  }

  // RFC 5987-konforme Kodierung für Content-Disposition filename*
  encodeRFC5987ValueChars(str) {
    try {
      return encodeURIComponent(String(str))
        .replace(/['()*]/g, function (c) {
          return "%" + c.charCodeAt(0).toString(16).toUpperCase()
        })
    } catch (e) {
      return "download"
    }
  }

  // ASCII-Fallback für filename="..." (nur sichtbarer Fallback, primär wird filename* verwendet)
  buildAsciiFallbackFilename(name) {
    try {
      var base = String(name || "download")
      return base.replace(/[^\x20-\x7E]/g, "_")
    } catch (e) {
      return "download"
    }
  }

  // Vollständigen Content-Disposition Header mit UTF-8-Unterstützung erzeugen
  buildContentDisposition(filename) {
    var fallback = this.buildAsciiFallbackFilename(filename)
    var utf8 = this.encodeRFC5987ValueChars(filename || "download")
    // Hinweis: zwei einfache Anführungszeichen nach UTF-8 kennzeichnen leeren Sprach-Tag gemäß RFC 5987
    return 'attachment; filename="' + fallback + '"; filename*=UTF-8\'' + utf8
  }

  async putBlock(blobUrl, sasToken, blockId, chunk) {
    var url = this.buildAzureUrl(blobUrl, sasToken, "comp=block&blockid=" + encodeURIComponent(blockId))
    var buffer = await this.readBlobAsArrayBuffer(chunk)
    var res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/octet-stream", "x-ms-version": "2020-10-02" },
      body: buffer,
    })
    if (!res.ok) {
      if (res.status === 403) {
        var text403 = ""
        try {
          text403 = await res.text()
        } catch (e) {}
        if (text403.indexOf("Signed expiry time") >= 0 || text403.indexOf("AuthenticationFailed") >= 0) {
          var err = new Error("SAS_EXPIRED")
          err.code = "SAS_EXPIRED"
          throw err
        }
        throw new Error("Block-Upload-Fehler: " + (text403 || res.statusText))
      }
      var text = ""
      try {
        text = await res.text()
      } catch (e) {}
      if (res.status === 404) {
        throw new Error("Block-Upload-Fehler (404): Container oder Blob-Pfad nicht gefunden")
      }
      if (res.status === 409) {
        throw new Error("Block-Upload-Fehler (409): Konflikt – prüfen Sie SAS-Berechtigungen (sp=cw)")
      }
      throw new Error("Block-Upload-Fehler (" + res.status + "): " + (text || res.statusText))
    }
  }

  async commitBlockList(blobUrl, sasToken, blockIds, meta) {
    var url = this.buildAzureUrl(blobUrl, sasToken, "comp=blocklist")
    var xml =
      "<?xml version=\"1.0\" encoding=\"utf-8\"?>" +
      "<BlockList>" +
      blockIds.map(function (id) { return "<Latest>" + id + "</Latest>" }).join("") +
      "</BlockList>"

    var headers = {
      "Content-Type": "application/xml",
      "x-ms-meta-originalsize": String(meta.originalSize),
      // Metadaten ASCII-sicher senden (Azure/HTTP Header)
      "x-ms-meta-originalname": this.encodeAsciiMeta(meta.originalName),
      "x-ms-meta-uploadid": meta.uploadId,
      "x-ms-meta-totalchunks": String(meta.totalChunks),
      "x-ms-meta-uploaddate": new Date().toISOString(),
    }
    if (meta.folderLabel) {
      headers["x-ms-meta-folderlabel"] = this.encodeAsciiMeta(meta.folderLabel)
    }
    if (meta.contentType) {
      headers["x-ms-blob-content-type"] = meta.contentType
    }

    // Setze Content-Disposition, damit Downloads den korrekten Unicode-Dateinamen tragen
    try {
      headers["x-ms-blob-content-disposition"] = this.buildContentDisposition(meta.originalName)
    } catch (e) {}

    headers["x-ms-version"] = "2020-10-02"
    var res = await fetch(url, { method: "PUT", headers: headers, body: xml })
    if (!res.ok) {
      if (res.status === 403) {
        var text403 = ""
        try { text403 = await res.text() } catch (e) {}
        if (text403.indexOf("Signed expiry time") >= 0 || text403.indexOf("AuthenticationFailed") >= 0) {
          var err = new Error("SAS_EXPIRED")
          err.code = "SAS_EXPIRED"
          throw err
        }
        throw new Error("Commit-Fehler: " + (text403 || res.statusText))
      }
      var text = ""
      try { text = await res.text() } catch (e) {}
      if (res.status === 404) {
        throw new Error("Commit-Fehler (404): Container oder Blob-Pfad nicht gefunden")
      }
      if (res.status === 409) {
        throw new Error("Commit-Fehler (409): Konflikt – prüfen Sie SAS-Berechtigungen (sp=cw)")
      }
      throw new Error("Commit-Fehler (" + res.status + "): " + (text || res.statusText))
    }
  }

  async uploadFile(file, onProgress) {
    var uploadId = this.generateUUID()
    var totalChunks = Math.ceil(file.size / this.CHUNK_SIZE)
    var fileName = file.name

    this.debug("Upload gestartet", {
      fileName: fileName,
      uploadId: uploadId,
      fileSize: file.size,
      contentType: file.type || null,
      chunkSize: this.CHUNK_SIZE,
      totalChunks: totalChunks,
    })

    if (onProgress) onProgress({ fileName: fileName, status: "uploading", progress: 0, phase: "uploading" })

    try {
      // Baue relativePath: webkitRelativePath > folderLabel + fileName > fileName
      // WICHTIG: KEIN uploadId-Präfix mehr! Backend fügt orgId/userId/uploadId hinzu
      var relativePath
      if (file.webkitRelativePath && file.webkitRelativePath.trim().length > 0) {
        relativePath = file.webkitRelativePath // z.B. "test/bericht.pdf"
      } else if (file.folderLabel && file.folderLabel.trim().length > 0) {
        relativePath = file.folderLabel + "/" + file.name // z.B. "test/bericht.pdf"
      } else {
        relativePath = file.name // z.B. "bericht.pdf"
      }

      // relativePath OHNE uploadId-Präfix (Backend baut: orgId/userId/uploadId/relativePath)
      var sasOptions = {
        relativePath: relativePath,
        originalName: file.name,
        contentType: file.type || undefined,
      }
      
      // orgId, userId können später vom Backend aus Token extrahiert werden
      // Falls verfügbar, können sie hier gesetzt werden
      if (file.orgId) {
        sasOptions.orgId = file.orgId
      }
      if (file.userId) {
        sasOptions.userId = file.userId
      }
      if (file.accountName) {
        sasOptions.accountName = file.accountName
      }

      var sas = await this.getSas(uploadId, undefined, sasOptions)
      var blobUrl = sas && sas.blobUrl
      if (!blobUrl) {
        throw new Error("Backend liefert keine blobUrl. Bitte getUploadSas auf finalen Blob umstellen (r,w,c).")
      }

      var blockIds = []
      for (var i = 0; i < totalChunks; i++) blockIds.push(this.makeBlockId(i))
      var nextIndex = 0
      var uploadedBytes = 0

      var self = this
      async function uploadOne(i) {
        var start = i * self.CHUNK_SIZE
        var end = Math.min(start + self.CHUNK_SIZE, file.size)
        var chunk = file.slice(start, end)

        for (var attempt = 1; attempt <= self.MAX_RETRIES; attempt++) {
          try {
            if (sas && sas.expiresOn) {
              var exp = new Date(sas.expiresOn).getTime()
              if (Date.now() > exp - 60000) {
                self.debug("SAS läuft bald ab – erneuere Token")
                sas = await self.getSas(uploadId, undefined, sasOptions)
              }
            }
            await self.putBlock(blobUrl, sas.sasToken, blockIds[i], chunk)
            uploadedBytes += chunk.size
            var progress = Math.min(99, Math.round((uploadedBytes / file.size) * 100))
            self.debug("Block " + (i + 1) + "/" + totalChunks + " erfolgreich", { start: start, end: end, progress: progress })
            if (onProgress) onProgress({ fileName: fileName, status: "uploading", progress: progress, phase: "uploading" })
            return
          } catch (err) {
            console.warn("Block " + (i + 1) + " Versuch " + attempt + " fehlgeschlagen. Wiederhole...", err)
            if (err && err.code === "SAS_EXPIRED") {
              self.debug("SAS abgelaufen – erneuere Token und versuche erneut", { block: i + 1, attempt: attempt })
              sas = await self.getSas(uploadId, undefined, sasOptions)
              continue
            }
            if (err && (err.name === 'NotSupportedError' || err.message === 'FileReader error')) {
              self.debug("Blob -> ArrayBuffer nicht unterstützt, prüfe Office/Browser-Runtime")
            }
            if (attempt === self.MAX_RETRIES) {
              throw new Error("Block " + (i + 1) + " konnte nach " + self.MAX_RETRIES + " Versuchen nicht hochgeladen werden.")
            }
            await self.sleep(self.RETRY_DELAY_MS * attempt)
          }
        }
      }

      async function worker() {
        while (true) {
          var i = nextIndex++
          if (i >= totalChunks) break
          await uploadOne(i)
        }
      }

      var workers = []
      var maxWorkers = Math.min(this.MAX_CONCURRENCY, totalChunks)
      for (var w = 0; w < maxWorkers; w++) workers.push(worker())
      await Promise.all(workers)

      this.debug("Commit der Blockliste starten")
      if (onProgress) onProgress({ fileName: fileName, status: "uploading", progress: 99, phase: "finalizing" })

      for (var attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
        try {
          await this.commitBlockList(blobUrl, sas.sasToken, blockIds, {
            contentType: file.type || undefined,
            originalSize: file.size,
            originalName: file.name,
            uploadId: uploadId,
            totalChunks: totalChunks,
            // Ordner-Label als Meta (falls vom Aufrufer gesetzt)
            folderLabel: file && file.folderLabel ? String(file.folderLabel) : undefined,
          })
          break
        } catch (err) {
          if (err && err.code === "SAS_EXPIRED") {
            this.debug("SAS abgelaufen – erneuere Token für Commit")
            sas = await this.getSas(uploadId, undefined, sasOptions)
            continue
          }
          if (attempt === this.MAX_RETRIES) throw err
          await this.sleep(this.RETRY_DELAY_MS * attempt)
        }
      }

      this.debug("Commit erfolgreich")
      if (onProgress) onProgress({ fileName: fileName, status: "completed", progress: 100 })

      return { success: true, blobUrl: blobUrl, fileName: uploadId, originalFileName: fileName, size: file.size }
    } catch (error) {
      var userFriendlyError = this.getUserFriendlyError(error)
      this.debug("Upload fehlgeschlagen", { fileName: fileName, error: error })
      if (onProgress) onProgress({ fileName: fileName, status: "error", progress: 0, error: userFriendlyError })
      throw error
    }
  }

  readBlobAsArrayBuffer(blob) {
    if (blob && typeof blob.arrayBuffer === "function") {
      return blob.arrayBuffer()
    }
    return new Promise(function (resolve, reject) {
      try {
        var reader = new FileReader()
        reader.onload = function () { resolve(reader.result) }
        reader.onerror = function () { reject(reader.error || new Error('FileReader error')) }
        reader.readAsArrayBuffer(blob)
      } catch (e) {
        reject(e)
      }
    })
  }

  generateUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0
      var v = c === "x" ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  sleep(ms) {
    return new Promise(function (resolve) { return setTimeout(resolve, ms) })
  }

  formatSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    var k = 1024
    var sizes = ["Bytes", "KB", "MB", "GB"]
    var i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  getUserFriendlyError(error) {
    try {
      var msg = error && (error.message || String(error))
      if (msg && msg.indexOf("instabil") >= 0) return "Die Internetverbindung ist zu instabil. Bitte versuchen Sie es erneut."
      if (msg && msg.indexOf("timeout") >= 0) return "Upload-Timeout. Bitte versuchen Sie es erneut."
      return msg || "Ein unbekannter Fehler ist aufgetreten."
    } catch (e) {
      return "Ein unbekannter Fehler ist aufgetreten."
    }
  }
}

// Global verfügbar machen
try { window.BlobUploadManager = BlobUploadManager } catch (e) {}
 